import { useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MediaPicker } from "@/components/admin/MediaPicker";
import { RichTextField } from "@/components/editor/RichTextField";
import { Sparkles, Loader2, UploadCloud, ImageIcon, Library, Video, Wand2 } from "lucide-react";

// Compact AI copy-suggestion button (label-adjacent). Calls onApply(text) when an option is chosen.
export const AiSuggestButton = ({ field, sectionType, pageName, current, onApply, testid }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [opts, setOpts] = useState([]);
  const suggest = async () => {
    setOpen(true); setLoading(true); setOpts([]);
    try {
      const { data } = await api.post("/admin/ai/suggest-copy", { field, section_type: sectionType, page_name: pageName, current_text: current || "" });
      setOpts(data.suggestions || []);
    } catch (e) { toast.error("AI suggestions unavailable"); setOpen(false); }
    finally { setLoading(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" onClick={suggest} data-testid={testid} className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-brand transition-colors hover:text-[hsl(var(--brand-red-deep))]">
          <Sparkles className="h-3.5 w-3.5" /> AI
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 z-[90]">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">AI suggestions</p>
        {loading ? (
          <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Generating…</div>
        ) : opts.length ? (
          <div className="space-y-2">
            {opts.map((o, i) => (
              <button key={o} type="button" onClick={() => { onApply(o); setOpen(false); }} data-testid={testid ? `${testid}-opt-${i}` : undefined} className="block w-full rounded-md border border-border p-2 text-left text-sm transition-colors hover:border-[hsl(var(--brand-red))] hover:bg-brand/5">{o}</button>
            ))}
            <button type="button" onClick={suggest} className="text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground">Regenerate</button>
          </div>
        ) : <p className="py-2 text-sm text-muted-foreground">No suggestions.</p>}
      </PopoverContent>
    </Popover>
  );
};

// ---- Text field with AI copy suggestions -----------------------------------
export const AiTextField = ({ label, value, onChange, multiline, rich, rows = 3, field, sectionType, pageName, placeholder, testid }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [opts, setOpts] = useState([]);

  const suggest = async () => {
    setOpen(true); setLoading(true); setOpts([]);
    try {
      const { data } = await api.post("/admin/ai/suggest-copy", {
        field, section_type: sectionType, page_name: pageName, current_text: value || "",
      });
      setOpts(data.suggestions || []);
    } catch (e) { toast.error("AI suggestions unavailable"); setOpen(false); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" onClick={suggest} data-testid={testid ? `${testid}-ai` : undefined} className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-brand transition-colors hover:text-[hsl(var(--brand-red-deep))]">
              <Sparkles className="h-3.5 w-3.5" /> AI
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 z-[90]">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">AI suggestions</p>
            {loading ? (
              <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Generating…</div>
            ) : opts.length ? (
              <div className="space-y-2">
                {opts.map((o, i) => (
                  <button key={o} type="button" onClick={() => { onChange(o); setOpen(false); }} data-testid={testid ? `${testid}-opt-${i}` : undefined} className="block w-full rounded-md border border-border p-2 text-left text-sm transition-colors hover:border-[hsl(var(--brand-red))] hover:bg-brand/5">
                    {o}
                  </button>
                ))}
                <button type="button" onClick={suggest} className="text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground">Regenerate</button>
              </div>
            ) : <p className="py-2 text-sm text-muted-foreground">No suggestions.</p>}
          </PopoverContent>
        </Popover>
      </div>
      <div className="mt-1.5">
        {rich
          ? <RichTextField value={value} onChange={onChange} placeholder={placeholder} testid={testid} />
          : multiline
            ? <Textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
            : <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />}
      </div>
    </div>
  );
};

// ---- Image field: preview + drag/drop upload + library + AI generate --------
export const AiImageField = ({ media = {}, onPatch }) => {
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [gen, setGen] = useState(false);
  const inputRef = useRef(null);

  const apply = (m) => onPatch({ image_url: m.url, id: m.id, alt: media.alt || m.alt || "" });

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("alt", file.name.replace(/\.[^.]+$/, ""));
      const { data } = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      apply(data); toast.success("Image uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed. Previous image kept."); }
    finally { setUploading(false); }
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe the image first"); return; }
    setGen(true);
    try {
      const { data } = await api.post("/admin/ai/suggest-image", { prompt, alt: prompt }, { timeout: 90000 });
      apply(data); toast.success("AI image generated"); setGenOpen(false); setPrompt("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Image generation failed"); }
    finally { setGen(false); }
  };

  return (
    <div className="space-y-3">
      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Background image</Label>
      {media.image_url ? (
        <div className="relative overflow-hidden rounded-lg border border-border">
          <img src={media.image_url} alt={media.alt || ""} className="h-40 w-full object-cover" style={{ objectPosition: `${media.focal_x ?? 50}% ${media.focal_y ?? 50}%` }} data-testid="drawer-image-preview" />
          <button type="button" onClick={() => onPatch({ image_url: "", id: "" })} className="absolute right-2 top-2 rounded-md bg-black/60 px-2 py-1 text-xs font-semibold text-white hover:bg-black/80">Remove</button>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
          onClick={() => inputRef.current?.click()}
          data-testid="drawer-image-dropzone"
          className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed p-6 text-center transition-colors ${drag ? "border-[hsl(var(--brand-red))] bg-brand/5" : "border-border hover:border-muted-foreground/40"}`}
        >
          <UploadCloud className="h-7 w-7 text-brand" />
          <p className="text-sm text-muted-foreground">{uploading ? "Uploading…" : "Drag & drop or click to upload"}</p>
          <p className="text-[11px] text-muted-foreground">PNG, JPG, GIF, WEBP</p>
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid="drawer-image-input" />

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => setPickerOpen(true)} className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase hover:bg-accent transition-colors"><Library className="h-4 w-4" /> Library</button>
        <Popover open={genOpen} onOpenChange={setGenOpen}>
          <PopoverTrigger asChild>
            <button type="button" data-testid="drawer-image-ai" className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-xs font-semibold uppercase text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors"><Wand2 className="h-4 w-4" /> Generate with AI</button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 z-[90]">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Describe the image</Label>
            <Textarea rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. athletes training in a modern gym, dramatic lighting" className="mt-1.5" data-testid="drawer-image-ai-prompt" />
            <button type="button" onClick={generate} disabled={gen} data-testid="drawer-image-ai-generate" className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-brand px-3 py-2 text-xs font-semibold uppercase text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors disabled:opacity-60">
              {gen ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</> : <><Sparkles className="h-4 w-4" /> Generate</>}
            </button>
          </PopoverContent>
        </Popover>
      </div>

      <div>
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Image URL</Label>
        <Input value={media.image_url || ""} onChange={(e) => onPatch({ image_url: e.target.value })} placeholder="https://… or /api/media/file/…" className="mt-1.5" data-testid="drawer-image-url" />
      </div>
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Alt text (accessibility)</Label>
        <Input value={media.alt || ""} onChange={(e) => onPatch({ alt: e.target.value })} placeholder="Describe the image" className="mt-1.5" data-testid="drawer-image-alt" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Focal X — {media.focal_x ?? 50}%</Label>
          <Slider value={[media.focal_x ?? 50]} min={0} max={100} step={1} onValueChange={([v]) => onPatch({ focal_x: v })} className="mt-2" data-testid="drawer-focal-x" />
        </div>
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Focal Y — {media.focal_y ?? 50}%</Label>
          <Slider value={[media.focal_y ?? 50]} min={0} max={100} step={1} onValueChange={([v]) => onPatch({ focal_y: v })} className="mt-2" data-testid="drawer-focal-y" />
        </div>
      </div>

      <MediaPicker open={pickerOpen} onOpenChange={setPickerOpen} onSelect={apply} />
    </div>
  );
};

// ---- Video field: URL + AI-suggested curated loops --------------------------
export const AiVideoField = ({ media = {}, onPatch }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vids, setVids] = useState([]);
  const [topic, setTopic] = useState("");

  const suggest = async () => {
    setOpen(true); setLoading(true); setVids([]);
    try { const { data } = await api.get(`/admin/ai/suggest-video?topic=${encodeURIComponent(topic)}`); setVids(data.videos || []); }
    catch (e) { toast.error("AI suggestions unavailable"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Background video (optional)</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" onClick={suggest} data-testid="drawer-video-ai" className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-brand hover:text-[hsl(var(--brand-red-deep))]"><Video className="h-3.5 w-3.5" /> Suggest</button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 z-[90]">
            <p className="mb-2 text-xs text-muted-foreground">AI video generation isn't available; pick a curated loop.</p>
            {loading ? (
              <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Finding…</div>
            ) : (
              <div className="space-y-2">
                {vids.map((v, i) => (
                  <button key={v.url} type="button" onClick={() => { onPatch({ video_url: v.url, image_url: media.image_url || v.poster }); setOpen(false); }} data-testid={`drawer-video-opt-${i}`} className="flex w-full items-center gap-2 rounded-md border border-border p-1.5 text-left transition-colors hover:border-[hsl(var(--brand-red))] hover:bg-brand/5">
                    <img src={v.poster} alt="" className="h-10 w-16 rounded object-cover" />
                    <span className="text-sm">{v.label}</span>
                  </button>
                ))}
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
      <Input value={media.video_url || ""} onChange={(e) => onPatch({ video_url: e.target.value })} placeholder=".webm / .mp4 URL" data-testid="drawer-video-url" />
      {media.video_url && <button type="button" onClick={() => onPatch({ video_url: "" })} className="text-[11px] font-semibold uppercase tracking-wide text-destructive hover:underline">Remove video</button>}
    </div>
  );
};
